import React from "react";
import MirsTable from "components/general/table/Table";
import { useViewport } from 'components/general/useViewPort'
import {
    isLiveStatus,
    isStartListStatus,
    isFinishedStatus
} from "helpers/status";
import { filterArray, findArray } from 'helpers/utils'
import LoaderSubComponent from "components/general/LoaderSubComponent";
import CaptionTable from "components/general/table/CaptionTable";
import Status from "components/presentation/Status";
import {
    TableColumnsStartList as ColumnsStartList,
    TableColumnsFinished as ColumnsFinished,
    TableColumnsLive as ColumnsLive,
    TableColumnsLiveSM as ColumnsLiveSM,
    TableColumnsFinishedSM as ColumnsFinishedSM,
    TableColumnsStats as ColumnsStats
} from 'components/sports/csp/components/TableColumns'
import { TableExpanded } from 'components/sports/csp/views/TableExpanded'
import Label from "components/general/Label"

const getConfiguration = (data, resultStatus) => {

    let configuration = {
        caption: null,
        columns: [],
        hiddenColumns: [],
        sortBy: [],
    };

    const scheduleStatus = data?.results?.schedule?.status?.code;
    let isReCsp = data?.results?.items?.some(item => findArray(item?.extendedResults, { er_code: ['RERACE'] }));
    let showRecords = data?.results?.items?.some(item => item.recordIndicators);
    let intermediatePoints = filterArray(data?.config?.extendedConfig, { code: ['INTERMEDIATE'] })

    configuration.caption = <CaptionTable
        leftCol={<h3 className="text-dark">{data && data?.results?.eventUnit?.shortDescription}</h3>}
        rightCol={<Status typeToPrint="result" scheduleStatus={scheduleStatus} resultStatus={resultStatus} />}
    />

    if (isStartListStatus(resultStatus)) {
        configuration.columns = ColumnsStartList()
        configuration.sortBy = [{ id: 'StartOrder', desc: false }]
    }

    if (isLiveStatus(resultStatus)) {
        configuration.columns = ColumnsLive(intermediatePoints)
        configuration.sortBy = [{ id: 'Rank', desc: false }]
    }

    if (isFinishedStatus(resultStatus)) {
        configuration.columns = ColumnsFinished()
        configuration.sortBy = [{ id: 'Rank', desc: false }]
        configuration.hiddenColumns = data?.results?.eventUnitCode.includes('-FNL') ? ['QualMark'] : []
        if (isReCsp)
            configuration.hiddenColumns = []
        if (!showRecords)
            configuration.hiddenColumns.push('Records')
    }
    return configuration
}

const getConfigurationSM = (data, resultStatus, breakPoint) => {

    let configuration = {
        caption: null,
        columns: [],
        hiddenColumns: [],
        sortBy: [],
    };

    const scheduleStatus = data?.results?.schedule?.status?.code;
    let showRecords = data?.results?.items?.some(item => item.recordIndicators);
    let intermediatePoints = filterArray(data?.config?.extendedConfig, { code: ['INTERMEDIATE'] })

    let leaderPos = findArray(data?.results?.extendedInfos, { ei_code: ['LEADER_POS'] }).ei_value

    configuration.caption = <CaptionTable
        leftCol={<h3 className="text-dark">{data && data?.results?.eventUnit?.shortDescription}</h3>}
        rightCol={<Status typeToPrint="result" scheduleStatus={scheduleStatus} resultStatus={resultStatus} />}
    />

    if (isStartListStatus(resultStatus)) {
        configuration.columns = ColumnsStartList()
        configuration.sortBy = [{ id: 'StartOrder', desc: false }]
    }

    if (isLiveStatus(resultStatus)) {
        configuration.columns = ColumnsLiveSM(breakPoint, intermediatePoints, leaderPos)
        configuration.sortBy = [{ id: 'Rank', desc: false }]
    }

    if (isFinishedStatus(resultStatus)) {
        configuration.columns = ColumnsFinishedSM()
        configuration.sortBy = [{ id: 'Rank', desc: false }]
        if (!showRecords)
            configuration.hiddenColumns.push('Records')
    }
    return configuration
}

const getConfigurationStats = (data) => {

    let configuration = {
        caption: null,
        columns: [],
        hiddenColumns: [],
        sortBy: [],
    };

    let currentInterval = findArray(data?.results?.currentExtendedInfos, { ei_code: ['DIST_LEADER'] }).ei_value

    configuration.caption = <CaptionTable
        leftCol={<h4 className="text-dark"><Label data="HeaderStatsTableAt" sport="CSP" /> {currentInterval}m</h4>}
    />
    configuration.sortBy = [{ id: 'Rank', desc: false }]
    configuration.columns = ColumnsStats()
    configuration.hiddenColumns.push('Rank')
    return configuration
}

const AddEmptyLanes = (athletes, laneMin, laneMax) => {
    let lanes = []
    for (let i = Number(laneMin); i <= Number(laneMax); i++) {
        if (athletes.some(ath => Number(ath.startOrder) === i)) {
            let resultAth = athletes.find(ath => Number(ath.startOrder) === i)
            resultAth.startSortOrder = Number(resultAth.startOrder)
            lanes.push(resultAth)
        }
        else {
            lanes.push({
                startSortOrder: i,
                startOrder: i.toString(),
            })
        }
    }
    return lanes
}

const CspScoreboard = ({ data }) => {
    const { breakPoint } = useViewport();
    let resultStatus = data?.results?.status?.code;
    const rsc = data?.results?.eventUnitCode;

    const config = (breakPoint === "sm" || breakPoint === "xs") ? getConfigurationSM(data, resultStatus, breakPoint) : getConfiguration(data, resultStatus)
    const configStats = getConfigurationStats(data)

    const renderCspSubComponent = React.useCallback(({ row }) => (
        <LoaderSubComponent component={
            <TableExpanded data={row?.original} config={data?.config} rsc={rsc} />
        }
        />
    ), []);

    let hasStatsTable = data?.results?.items?.some(item => item?.extendedResultsCurrent);

    let laneMin = findArray(data?.config?.extendedConfig, { code: ['LANE_MIN'], type: ['EC'] })?.value
    let laneMax = findArray(data?.config?.extendedConfig, { code: ['LANE_MAX'], type: ['EC'] })?.value
    let athletes = isStartListStatus(resultStatus) ? AddEmptyLanes(data?.results?.items, laneMin, laneMax) : data?.results?.items

    return (
        <>
            {data?.results?.items
                ? (
                    <>
                        < MirsTable
                            //Dato to show by row
                            data={athletes}
                            //Columnas a visualizar 
                            columns={config.columns}
                            caption={config.caption}
                            keyRow='participant-code'
                            sortable={false}
                            hiddenColumns={config.hiddenColumns}
                            initialSortBy={config.sortBy}
                            renderRowSubComponent={renderCspSubComponent}
                            id={`mirs-table-result-${rsc}`}
                        />

                        {breakPoint === "sm" || breakPoint === "xs"
                            ? <></>
                            : hasStatsTable && isLiveStatus(resultStatus) &&
                            <div className="row-statsTable">< MirsTable
                                //Dato to show by row
                                data={data?.results?.items}
                                //Columnas a visualizar 
                                columns={configStats.columns}
                                caption={configStats.caption}
                                keyRow='participant-code'
                                sortable={false}
                                hiddenColumns={configStats.hiddenColumns}
                                initialSortBy={configStats.sortBy}
                                id={`mirs-table-result-${rsc}`}
                            /></div>
                        }
                    </>
                ) : (
                    ''
                )
            }
        </>
    );
}


const CspResults = ({ data }) => {
    return <CspScoreboard data={data} />
};

export default CspResults;